import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { IconCopy } from '@tabler/icons-react';
import { toast } from 'sonner';
import { DataTable } from '@/components/ui/data-table';
import type { DataTableColumn } from '@/components/ui/data-table';
import { chartColorArray, colors } from '@/lib/colors';
import { Skeleton } from '@/components/ui/skeleton';
import { CurrencyValue } from '@/components/CurrencyValue';
import type { ContributorsSpendResponse } from '@/data/types/dashboard.types';
import { DashboardEmptyState } from '@/components/Dashboard/EmptyState';

interface HorizontalBarChartProps {
  data?: ContributorsSpendResponse;
  isLoading?: boolean;
}

// Truncate and make copyable
const truncateId = (id: string, maxLength: number = 20) => {
  if (id.length <= maxLength) return id;
  return id.substring(0, maxLength - 3) + '...';
};

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).then(() => {
    toast.success('Contributor ID copied to clipboard');
  });
};

// Data item type for internal use
type DataItem = {
  userId: string;
  displayName: string;
  cost: number;
  events: number;
  color: string;
  costPerAction?: number;
  costDistribution?: number;
  isTotal?: boolean;
};

// Calculate nice round max value for X axis
const calculateMaxX = (data: DataItem[]) => {
  if (data.length === 0) return 1000;
  const maxValue = Math.max(...data.map((item) => item.cost));
  if (maxValue === 0) return 1000;
  
  // Round up to next nice number
  const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
  const normalized = maxValue / magnitude;
  
  let roundedNormalized;
  if (normalized <= 1.2) roundedNormalized = 1.2;
  else if (normalized <= 1.5) roundedNormalized = 1.5;
  else if (normalized <= 2) roundedNormalized = 2;
  else if (normalized <= 2.5) roundedNormalized = 2.5;
  else if (normalized <= 3) roundedNormalized = 3;
  else if (normalized <= 4) roundedNormalized = 4;
  else if (normalized <= 5) roundedNormalized = 5;
  else roundedNormalized = 10;
  
  return roundedNormalized * magnitude;
};

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
        <p className="text-sm font-medium mb-2">
          {data.displayName === 'Other' ? 'Other Users' : data.userId}
        </p>
        <p className="text-xs text-muted-foreground">
          Cost: <span className="font-medium text-foreground">${data.cost.toLocaleString()}</span>
        </p>
        <p className="text-xs text-muted-foreground">
          Events: <span className="font-medium text-foreground">{data.events.toLocaleString()}</span>
        </p>
      </div>
    );
  }
  return null;
};

// Loading skeleton component
const LoadingSkeleton = () => (
  <div className="space-y-4">
    {/* Chart skeleton */}
    <div className="w-full" style={{ height: '180px' }}>
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center gap-2">
            <Skeleton className="h-5 flex-1" style={{ maxWidth: `${80 - i * 15}%` }} />
          </div>
        ))}
      </div>
    </div>
    {/* Table skeleton */}
    <div className="space-y-2">
      <Skeleton className="h-10 w-full" />
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  </div>
);


const HorizontalBarChart = ({ data, isLoading }: HorizontalBarChartProps) => {
  // Show loading state
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Check if all values are effectively zero/empty
  const hasData = data && data.items.length > 0 && 
    data.items.some(item => item.total_costs > 0);

  // Show empty state if no data
  if (!hasData) {
    return <DashboardEmptyState height={300} />;
  }

  // Transform API data to internal format
  const chartData: DataItem[] = data.items.map((item, index) => ({
    userId: item.contributor_id,
    displayName: item.contributor_id === 'Other' ? 'Other' : truncateId(item.contributor_id),
    cost: item.total_costs,
    events: item.events,
    color: chartColorArray[index % chartColorArray.length],
    costPerAction: item.cost_per_action,
    costDistribution: item.cost_distribution,
    isTotal: false,
  }));

  const maxX = calculateMaxX(chartData);
  
  // Add total row using totals from API response
  const tableDataWithTotal: DataItem[] = [
    ...chartData,
    {
      userId: 'total',
      displayName: 'Total',
      cost: data.totals.total_costs,
      events: data.totals.events,
      color: chartColorArray[5],
      costPerAction: data.totals.cost_per_action,
      costDistribution: data.totals.cost_distribution,
      isTotal: true,
    },
  ];

  // Define columns with flex widths
  const columns: DataTableColumn<DataItem>[] = [
    {
      id: 'contributorId',
      header: 'Contributor ID',
      flex: 1,
      align: 'left',
      enableSorting: false,
      cell: ({ row }) => {
        const item = row.original;
        if (item.isTotal) {
          return <span className="font-bold">Total</span>;
        }
        
        const truncatedId = truncateId(item.userId);
        const isTruncated = item.userId.length > 20;
        
        return (
          <div className="flex items-center gap-[8px] group">
            <div 
              className="size-2 rounded-full shrink-0" 
              style={{ backgroundColor: item.color }}
            />
            <span className="truncate max-w-[200px]">{truncatedId}</span>
            {isTruncated && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(item.userId);
                }}
                className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center size-[20px] rounded-[4px] hover:bg-[#EDEEF3] text-[#8A8F98] hover:text-[#08090A] cursor-pointer"
                title="Copy full ID"
              >
                <IconCopy className="size-3.5" />
              </button>
            )}
          </div>
        );
      },
    },
    {
      id: 'costs',
      header: 'Total Costs',
      accessorKey: 'cost',
      flex: 0.8,
      align: 'left',
      enableSorting: false,
      cell: ({ row, getValue }) => {
        const value = getValue() as number;
        return (
          <CurrencyValue value={value} className={row.original.isTotal ? 'font-bold' : ''} />
        );
      },
    },
    {
      id: 'costPerAction',
      header: '$ per Action',
      accessorKey: 'costPerAction',
      flex: 0.8,
      align: 'left',
      enableSorting: false,
      cell: ({ row, getValue }) => {
        const value = getValue() as number;
        return (
          <CurrencyValue value={value} className={row.original.isTotal ? 'font-bold' : ''} />
        );
      },
    },
    {
      id: 'costDistribution',
      header: 'Cost Distribution',
      accessorKey: 'costDistribution',
      flex: 1,
      align: 'left',
      enableSorting: false,
      cell: ({ row, getValue }) => {
        const value = getValue() as number;
        return (
          <span className={row.original.isTotal ? 'font-bold' : ''}>
            {value.toFixed(1)}%
          </span>
        );
      },
    },
  ];
  
  return (
    <div className="space-y-4">
      {/* Chart */}
      <div className="w-full" style={{ height: '180px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{
              top: 5,
              right: 0,
              left: 0,
              bottom: 5,
            }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              strokeWidth={1}
              horizontal={false}
            />
            <XAxis 
              type="number"
              domain={[0, maxX]}
              tickFormatter={(value) => `$${value >= 1000 ? `${value / 1000}k` : value}`}
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
            />
            <YAxis 
              type="category"
              dataKey="displayName"
              axisLine={false}
              tickLine={false}
              tick={false}
              width={0}
            />
            <Tooltip content={<CustomTooltip />} cursor={{fill: colors.purple[25], opacity: 1}}/>
            <Bar 
              dataKey="cost" 
              radius={[0, 4, 4, 0]}
              maxBarSize={20}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <DataTable
        data={tableDataWithTotal}
        columns={columns}
        enableColumnResizing={false}
        enableSorting={false}
        getRowId={(row) => row.userId}
        getRowClassName={(row) => 
          row.isTotal 
            ? 'font-bold' 
            : ''
        }
      />
    </div>
  );
};

export { HorizontalBarChart };
